// Practice Studio — XP / rank / ladder derivation layer.
//
// LOAD-BEARING DESIGN RULE: the `practiceReps` collection is the ONLY stored
// truth about practice progress. XP totals, ranks, and ladder-step completion
// are all DERIVED here from the rep log at read time — never stamped onto
// docs. That's what makes the reward structure safely tunable: change the
// numbers below and every artist's history re-scores under the new rules,
// no migrations, no drift. Any future gamification (lessons, badges, streaks)
// must follow the same rule: add a derivation, not a stored counter.

// ---------------------------------------------------------------------------
// XP values — the whole economy lives in this one object.
// ---------------------------------------------------------------------------
export const PRACTICE_XP = {
  paperRep: 10, // any logged paper rep
  griddleRep: 25, // batter is precious — griddle reps pay more
  perRatingStar: 10, // Dan's review: rating (1–5) × this = bonus XP
  ladderStep: 100, // completing a curated ladder step
  liveFlash: 40, // real flash pancake served at the booth (from shift summaries)
  liveCustom: 75, // real custom — the summit of the craft
};

// ---------------------------------------------------------------------------
// Ranks — display-only thresholds on total XP.
// ---------------------------------------------------------------------------
// Each rank also GRANTS a piece of gear — a named, purely cosmetic item that
// joins the artist's character sheet at rank-up. The next rank's gear is the
// visible carrot ("unlocks 🪄 Batter Wand").
// gearSlot: which avatar zone the item equips to — "hat" | "held" | "accessory"
export const PRACTICE_RANKS = [
  { xp: 0, name: "Batter Cadet", icon: "🥚", gear: "🥄 Wooden Spoon", gearSlot: "held" },
  { xp: 75, name: "Squeeze Bottle Squire", icon: "🍼", gear: "🍼 Trusty Squeeze Bottle", gearSlot: "held" },
  { xp: 200, name: "Drizzle Apprentice", icon: "🌀", gear: "📐 Grid Goggles", gearSlot: "accessory" },
  { xp: 400, name: "Line & Ladle Journeyman", icon: "🥞", gear: "🥣 Steady Ladle", gearSlot: "held" },
  { xp: 700, name: "Flipsmith", icon: "🔄", gear: "🔄 Balanced Spatula", gearSlot: "held" },
  { xp: 1100, name: "Golden-Brown Guildhand", icon: "✨", gear: "✨ Golden Whisk", gearSlot: "held" },
  { xp: 1600, name: "Griddle Master", icon: "👑", gear: "👑 Griddle Crown", gearSlot: "hat" },
  // Upper tiers exist because live XP is retroactive — a career of real
  // pancakes lands well past 1600.
  { xp: 2600, name: "Flip Wizard", icon: "🪄", gear: "🪄 Batter Wand", gearSlot: "held" },
  { xp: 5000, name: "Short-Stack Sorcerer", icon: "🎩", gear: "🎩 Sorcerer's Toque", gearSlot: "hat" },
  { xp: 9500, name: "Syrup Sage", icon: "🍁", gear: "🍁 Syrup Circlet", gearSlot: "hat" },
  { xp: 17000, name: "Legend of the Golden Griddle", icon: "🏆", gear: "🏆 Legend's Ladle", gearSlot: "held" },
];

export function earnedGear(xp) {
  return PRACTICE_RANKS.filter((r) => xp >= r.xp).map((r) => r.gear);
}

// ---------------------------------------------------------------------------
// Badges — derived achievements (same rule: computed from reps + live stats,
// never stored). Add new ones freely; history re-scores instantly.
// ---------------------------------------------------------------------------
export const PRACTICE_BADGES = [
  { id: "first_rep", prize: "🐣 Hatchling Charm", prizeSlot: "accessory", icon: "🐣", name: "First Rep", desc: "Log your very first practice rep",
    test: (reps) => reps.length >= 1 },
  { id: "habit", prize: "⏰ Rhythm Charm", prizeSlot: "accessory", icon: "📅", name: "Habit Forming", desc: "Practice on 3 different days",
    test: (reps) => new Set(reps.filter((r) => r.createdAt?.seconds).map((r) => new Date(r.createdAt.seconds * 1000).toDateString())).size >= 3 },
  { id: "ten_spot", prize: "🔟 Ten-Spot Pin", prizeSlot: "accessory", icon: "🔟", name: "Ten Spot", desc: "10 reps on a single design",
    test: (reps) => {
      const c = {};
      for (const r of reps) { if (r.designId) { c[r.designId] = (c[r.designId] || 0) + 1; if (c[r.designId] >= 10) return true; } }
      return false;
    } },
  { id: "batter_up", prize: "🥞 First-Batter Pin", prizeSlot: "accessory", icon: "🥞", name: "Batter Up", desc: "First rep on the real griddle",
    test: (reps) => reps.some((r) => r.medium === "griddle") },
  { id: "teachers_nod", prize: "⭐ Gold Star Pin", prizeSlot: "accessory", icon: "⭐", name: "Teacher's Nod", desc: "Earn a review of 3★ or better",
    test: (reps) => reps.some((r) => (r.review?.rating || 0) >= 3) },
  { id: "perfect_five", prize: "🌟 Star Halo", prizeSlot: "hat", icon: "🌟", name: "Perfect Five", desc: "Earn a 5★ review",
    test: (reps) => reps.some((r) => r.review?.rating === 5) },
  { id: "explorer", prize: "🗺 Explorer Map", prizeSlot: "held", icon: "🗺", name: "Explorer", desc: "Practice 5 different designs",
    test: (reps) => new Set(reps.filter((r) => r.designId).map((r) => r.designId)).size >= 5 },
  { id: "free_spirit", prize: "🎲 Lucky Dice", prizeSlot: "held", icon: "🎲", name: "Free Spirit", desc: "5 freeform play reps",
    test: (reps) => reps.filter((r) => !r.designId).length >= 5 },
  { id: "quick_draw", prize: "⚡ Lightning Pin", prizeSlot: "accessory", icon: "⚡", name: "Quick Draw", desc: "A rep under 4 minutes rated 3★+",
    test: (reps) => reps.some((r) => r.drawTimeMs > 0 && r.drawTimeMs < 4 * 60000 && (r.review?.rating || 0) >= 3) },
  { id: "century", prize: "🎓 Century Cap", prizeSlot: "hat", icon: "💯", name: "Century Club", desc: "100 total reps",
    test: (reps) => reps.length >= 100 },
  { id: "booth_debut", prize: "🎟 Debut Ticket", prizeSlot: "accessory", icon: "🏪", name: "Booth Debut", desc: "Serve a real pancake at the booth",
    test: (reps, live) => (live?.totalOrders || 0) >= 1 },
  { id: "crowd_favorite", prize: "🎪 Big-Top Hat", prizeSlot: "hat", icon: "🎪", name: "Crowd Favorite", desc: "25 real pancakes served",
    test: (reps, live) => (live?.totalOrders || 0) >= 25 },
];

export function deriveBadges(reps, live) {
  return PRACTICE_BADGES.map((b) => ({ ...b, earned: !!b.test(reps || [], live) }));
}

// ---------------------------------------------------------------------------
// Avatar item catalog — everything equippable, from both unlock tracks
// (rank gear + badge prizes), each tagged with its avatar zone. `earned` is
// derived like everything else. Slots: "hat" | "held" | "accessory".
// ---------------------------------------------------------------------------
export function avatarItems(xp, badges) {
  const earnedBadgeIds = new Set((badges || []).filter((b) => b.earned).map((b) => b.id));
  const items = [];
  for (const r of PRACTICE_RANKS) {
    items.push({
      name: r.gear, icon: r.gear.split(" ")[0], slot: r.gearSlot,
      earned: xp >= r.xp, unlockLabel: r.name,
    });
  }
  for (const b of PRACTICE_BADGES) {
    if (!b.prize) continue;
    items.push({
      name: b.prize, icon: b.prize.split(" ")[0], slot: b.prizeSlot,
      earned: earnedBadgeIds.has(b.id), unlockLabel: `${b.name} badge`,
    });
  }
  return items;
}

// ---------------------------------------------------------------------------
// Live-shift XP — derived from shift_summaries.artistBreakdown, which the
// shift close has ALWAYS written (keyed by free-text artistName). Matching is
// by lowercased name (artist's canonical name + aliases from the registry),
// so historical pancakes tether to the registry identity retroactively.
// Same rule as everything else here: computed, never stored.
// ---------------------------------------------------------------------------
export function deriveLiveStats(nameKeys, summaries) {
  const keys = new Set((nameKeys || []).map((n) => String(n).toLowerCase()));
  let flashCount = 0;
  let customCount = 0;
  let shiftsWorked = 0;
  for (const s of summaries || []) {
    if (s._isTestData) continue;
    const breakdown = s.artistBreakdown || {};
    let workedThis = false;
    for (const [rawName, a] of Object.entries(breakdown)) {
      if (!keys.has(rawName.trim().toLowerCase())) continue;
      flashCount += a.flashCount ?? 0;
      customCount += a.customCount ?? 0;
      workedThis = true;
    }
    if (workedThis) shiftsWorked++;
  }
  const xp = flashCount * PRACTICE_XP.liveFlash + customCount * PRACTICE_XP.liveCustom;
  return { flashCount, customCount, totalOrders: flashCount + customCount, shiftsWorked, xp };
}

export function rankForXp(xp) {
  let current = PRACTICE_RANKS[0];
  let next = null;
  for (let i = 0; i < PRACTICE_RANKS.length; i++) {
    if (xp >= PRACTICE_RANKS[i].xp) {
      current = PRACTICE_RANKS[i];
      next = PRACTICE_RANKS[i + 1] || null;
    }
  }
  return { current, next };
}

// ---------------------------------------------------------------------------
// Rep helpers
// ---------------------------------------------------------------------------
export function repBaseXp(rep) {
  return rep.medium === "griddle" ? PRACTICE_XP.griddleRep : PRACTICE_XP.paperRep;
}

export function repReviewXp(rep) {
  const rating = rep.review?.rating;
  if (!rating) return 0;
  return rating * PRACTICE_XP.perRatingStar;
}

export function repTotalXp(rep) {
  return repBaseXp(rep) + repReviewXp(rep);
}

// ---------------------------------------------------------------------------
// Ladder — curated in config/practiceLadder as { steps: [...] }.
// Step shape: { designId, paperReps, griddleReps, minRating, xp }
//   paperReps / griddleReps — how many reps of each medium the step asks for
//   minRating — step needs ≥1 rep of this design reviewed at or above this
//               (0 / absent = no review requirement)
//   xp — bonus for completing the step (falls back to PRACTICE_XP.ladderStep)
// ---------------------------------------------------------------------------
export function deriveStepProgress(step, reps) {
  const forDesign = reps.filter((r) => r.designId === step.designId);
  const paperDone = forDesign.filter((r) => r.medium !== "griddle").length;
  const griddleDone = forDesign.filter((r) => r.medium === "griddle").length;
  const bestRating = forDesign.reduce(
    (best, r) => Math.max(best, r.review?.rating || 0),
    0
  );
  const paperGoal = step.paperReps || 0;
  const griddleGoal = step.griddleReps || 0;
  const ratingGoal = step.minRating || 0;
  const complete =
    paperDone >= paperGoal &&
    griddleDone >= griddleGoal &&
    bestRating >= ratingGoal;
  return {
    paperDone: Math.min(paperDone, paperGoal),
    paperGoal,
    griddleDone: Math.min(griddleDone, griddleGoal),
    griddleGoal,
    bestRating,
    ratingGoal,
    complete,
  };
}

// ---------------------------------------------------------------------------
// Full progress derivation — the one entry point views should use.
// Returns { totalXp, rank: {current,next}, steps: [{step, progress}],
//           currentStepIndex, repCount, griddleRepCount }
// ---------------------------------------------------------------------------
export function derivePracticeProgress(reps, ladderSteps) {
  const steps = (ladderSteps || []).map((step) => ({
    step,
    progress: deriveStepProgress(step, reps),
  }));
  const repXp = reps.reduce((sum, r) => sum + repTotalXp(r), 0);
  const ladderXp = steps.reduce(
    (sum, s) =>
      sum + (s.progress.complete ? s.step.xp || PRACTICE_XP.ladderStep : 0),
    0
  );
  const totalXp = repXp + ladderXp;
  const firstIncomplete = steps.findIndex((s) => !s.progress.complete);
  return {
    totalXp,
    rank: rankForXp(totalXp),
    steps,
    currentStepIndex: firstIncomplete, // -1 = ladder fully complete (or empty)
    repCount: reps.length,
    griddleRepCount: reps.filter((r) => r.medium === "griddle").length,
  };
}

// ---------------------------------------------------------------------------
// Draw-time trend for one design: newest-last array of {ms, createdAt}.
// ---------------------------------------------------------------------------
export function drawTimeTrend(reps, designId) {
  return reps
    .filter((r) => r.designId === designId && r.drawTimeMs > 0)
    .sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0))
    .map((r) => ({ ms: r.drawTimeMs, createdAt: r.createdAt }));
}

export function formatDrawTime(ms) {
  if (!ms || ms <= 0) return "—";
  const totalSec = Math.round(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return m > 0 ? `${m}m ${String(s).padStart(2, "0")}s` : `${s}s`;
}
